import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileUp, RefreshCw, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import CountryDropdown from '@/components/ui/CountryDropdown';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import Modal from '@/components/ui/Modal';
import MultiSelect from '@/components/ui/MultiSelect';
import { useCategoriesForDropdown } from '@/hooks';
import { MediaSource } from '@/types/media';
import {
  SavedTVChannelImport,
  TVChannelImportItem,
  tvChannelImportService,
} from '@/services/tvChannelImportService';

type SourceType = 'url' | 'file' | 'saved';
type ChannelNumberValidationRow = {
  id: string;
  channelId?: string | null;
  selected: boolean;
  channelNumber: number | null;
};

const buildRows = (items: TVChannelImportItem[]) =>
  items.map((item, index) => ({
    ...item,
    id: item.id || `channel-${index}`,
    selected: item.selected ?? true,
    channelNumber: item.channelNumber ?? null,
    title: item.title || 'Sin titulo',
    countryId: item.countryId || '',
    categories: item.categories || [],
    logoUrl: item.logoUrl || '',
    url: item.url || '',
    imported: item.imported ?? null,
    error: item.error ?? null,
  }));

const TVChannelsImport = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [sourceType, setSourceType] = useState<SourceType>('url');
  const [sourceValue, setSourceValue] = useState('');
  const [rows, setRows] = useState<TVChannelImportItem[]>([]);
  const [savedImports, setSavedImports] = useState<SavedTVChannelImport[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [saveImport, setSaveImport] = useState(false);
  const [importName, setImportName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [loadingSavedImportId, setLoadingSavedImportId] = useState<string | null>(null);
  const [validatingNumbers, setValidatingNumbers] = useState<Record<string, boolean>>({});
  const [numberErrors, setNumberErrors] = useState<Record<string, string>>({});
  const { data: categories = [] } = useCategoriesForDropdown('TV_CHANNEL');

  const selectedCount = rows.filter((row) => row.selected).length;
  const categoryOptions = categories.map((category) => ({ value: category.id, label: category.description }));
  const canImport = selectedCount > 0 && rows.every((row) => !row.selected || !numberErrors[row.id]);

  const channelNumberValidationKey = JSON.stringify(
    rows.map((row) => ({
      id: row.id,
      channelId: row.channelId ?? null,
      selected: row.selected,
      channelNumber: row.channelNumber ?? null,
    }))
  );

  const channelNumberValidationRows = useMemo(
    () => JSON.parse(channelNumberValidationKey) as ChannelNumberValidationRow[],
    [channelNumberValidationKey]
  );

  const duplicateNumbers = useMemo(() => {
    const counts = new Map<number, number>();
    channelNumberValidationRows.forEach((row) => {
      if (row.selected && row.channelNumber !== null && row.channelNumber !== undefined) {
        counts.set(row.channelNumber, (counts.get(row.channelNumber) ?? 0) + 1);
      }
    });
    return counts;
  }, [channelNumberValidationRows]);

  useEffect(() => {
    void loadSavedImports();
  }, []);

  useEffect(() => {
    const numbersToValidate = channelNumberValidationRows
      .filter((row) => row.selected && row.channelNumber !== null && row.channelNumber !== undefined)
      .map((row) => ({ id: row.id, channelId: row.channelId, channelNumber: row.channelNumber as number }));

    const timeout = window.setTimeout(() => {
      numbersToValidate.forEach((row) => {
        if ((duplicateNumbers.get(row.channelNumber) ?? 0) > 1) {
          setNumberErrors((prev) => ({ ...prev, [row.id]: 'Numero duplicado en esta importacion' }));
          return;
        }

        setValidatingNumbers((prev) => ({ ...prev, [row.id]: true }));
        tvChannelImportService
          .checkChannelNumber(row.channelNumber, row.channelId)
          .then((availability) => {
            setNumberErrors((prev) => {
              const next = { ...prev };
              if (availability.taken) {
                next[row.id] = `Ya existe: ${availability.title || 'canal existente'}`;
              } else {
                delete next[row.id];
              }
              return next;
            });
          })
          .catch(() => {
            setNumberErrors((prev) => ({ ...prev, [row.id]: 'No se pudo validar el numero' }));
          })
          .finally(() => {
            setValidatingNumbers((prev) => ({ ...prev, [row.id]: false }));
          });
      });
    }, 450);

    return () => window.clearTimeout(timeout);
  }, [channelNumberValidationRows, duplicateNumbers]);

  const loadSavedImports = async () => {
    setIsLoadingSaved(true);
    try {
      setSavedImports(await tvChannelImportService.getSavedImports());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudieron cargar las importaciones guardadas');
    } finally {
      setIsLoadingSaved(false);
    }
  };

  const updateRow = (id: string, patch: Partial<TVChannelImportItem>) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const mergeSavedRowsWithDownloadedRows = (
    downloadedRows: TVChannelImportItem[],
    savedRows: TVChannelImportItem[]
  ) => {
    const savedByTitle = new Map(
      savedRows.map((item) => [item.title.trim().toLocaleLowerCase(), item])
    );

    return buildRows(downloadedRows).map((row) => {
      const savedRow = savedByTitle.get(row.title.trim().toLocaleLowerCase());
      if (!savedRow) {
        return row;
      }

      return {
        ...row,
        channelId: savedRow.channelId,
        selected: Boolean(savedRow.imported),
        channelNumber: savedRow.channelNumber ?? row.channelNumber,
        countryId: savedRow.countryId || row.countryId,
        categories: savedRow.categories || row.categories,
        logoUrl: row.logoUrl || savedRow.logoUrl || '',
      };
    });
  };

  const handleParseUrl = async () => {
    const url = sourceValue.trim();
    if (!url) {
      toast.error('Ingresa la URL de la lista M3U8');
      return;
    }

    setIsLoadingList(true);
    setSourceType('url');
    try {
      const channels = await tvChannelImportService.parseUrl(url);
      setRows(buildRows(channels));
      setImportName(`Importacion ${new Date().toLocaleDateString()}`);
      toast.success(`${channels.length} canales cargados`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo cargar la lista');
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setIsLoadingList(true);
    setSourceType('file');
    setSourceValue(file.name);
    try {
      const channels = await tvChannelImportService.parseFile(file);
      setRows(buildRows(channels));
      setImportName(file.name.replace(/\.[^.]+$/, '') || 'Importacion IPTV');
      toast.success(`${channels.length} canales cargados`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo cargar el archivo');
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleUseSavedImport = async (savedImport: SavedTVChannelImport) => {
    const isUrlImport = savedImport.sourceType === 'url' && Boolean(savedImport.sourceValue);
    setLoadingSavedImportId(savedImport.id);
    setImportName(savedImport.name);

    try {
      if (isUrlImport && savedImport.sourceValue) {
        const channels = await tvChannelImportService.parseUrl(savedImport.sourceValue);
        setRows(mergeSavedRowsWithDownloadedRows(channels, savedImport.items));
        setSourceType('url');
        setSourceValue(savedImport.sourceValue);
        toast.success('Lista descargada desde la URL guardada');
        return;
      }

      setRows(buildRows(savedImport.items).map((item) => ({ ...item, selected: Boolean(item.imported) })));
      setSourceType('saved');
      setSourceValue(savedImport.sourceValue || savedImport.name);
      toast.success('Importacion cargada');
    } catch (error) {
      setRows(buildRows(savedImport.items).map((item) => ({ ...item, selected: Boolean(item.imported) })));
      setSourceType(isUrlImport ? 'url' : 'saved');
      setSourceValue(savedImport.sourceValue || savedImport.name);
      toast.error(error instanceof Error ? error.message : 'No se pudo descargar la URL guardada. Se cargo la importacion anterior.');
    } finally {
      setLoadingSavedImportId(null);
    }
  };

  const applySequence = (rowIndex: number) => {
    const start = rows[rowIndex]?.channelNumber;
    if (start === null || start === undefined || Number.isNaN(start)) {
      toast.error('Ingresa el primer numero de canal');
      return;
    }

    setRows((current) =>
      current.map((row, index) => (index >= rowIndex ? { ...row, channelNumber: start + (index - rowIndex) } : row))
    );
  };

  const applyCountryFromRow = (rowIndex: number) => {
    const countryId = rows[rowIndex]?.countryId;
    if (!countryId) {
      toast.error('Selecciona un pais primero');
      return;
    }

    setRows((current) =>
      current.map((row, index) => (index >= rowIndex ? { ...row, countryId } : row))
    );
  };

  const toggleAll = (selected: boolean) => {
    setRows((current) => current.map((row) => ({ ...row, selected })));
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const result = await tvChannelImportService.importChannels({
        name: importName.trim() || 'Importacion IPTV',
        sourceType,
        sourceValue,
        saveImport,
        items: rows,
      });
      setRows(buildRows(result.items));
      setIsImportModalOpen(false);
      toast.success(`${result.importedCount} canales importados, ${result.skippedCount} omitidos`);
      if (saveImport) {
        await loadSavedImports();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo importar la lista');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Importar listas IPTV</h1>
              <p className="mt-1 text-sm text-gray-600">Carga una lista M3U8, ajusta los canales y confirma solo los que entraran al sistema.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => void loadSavedImports()} disabled={isLoadingSaved}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoadingSaved ? 'animate-spin' : ''}`} />
              Actualizar guardadas
            </Button>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="flex rounded-md shadow-sm">
              <input
                type="url"
                value={sourceType === 'url' ? sourceValue : ''}
                onChange={(event) => {
                  setSourceType('url');
                  setSourceValue(event.target.value);
                }}
                placeholder="https://servidor/lista.m3u8"
                disabled={isLoadingList}
                className="min-w-0 flex-1 rounded-l-md border border-r-0 border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <Button type="button" variant="outline" className="rounded-l-none border-l-0" loading={isLoadingList && sourceType === 'url'} onClick={() => void handleParseUrl()}>
                <Download className="mr-2 h-4 w-4" />
                Descargar
              </Button>
            </div>

            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isLoadingList}>
              <FileUp className="mr-2 h-4 w-4" />
              Subir archivo
            </Button>
            <input ref={fileInputRef} type="file" accept=".m3u,.m3u8,text/plain,audio/x-mpegurl,application/vnd.apple.mpegurl" className="hidden" onChange={handleFileChange} />
          </div>

          {savedImports.length > 0 && (
            <div className="mt-5">
              <h2 className="mb-2 text-sm font-semibold text-gray-900">Importaciones guardadas</h2>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {savedImports.map((savedImport) => (
                  <button
                    key={savedImport.id}
                    type="button"
                    onClick={() => void handleUseSavedImport(savedImport)}
                    disabled={loadingSavedImportId === savedImport.id}
                    className="rounded-md border border-gray-200 bg-gray-50 p-3 text-left hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <div className="truncate text-sm font-medium text-gray-900">{savedImport.name}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      {loadingSavedImportId === savedImport.id
                        ? 'Descargando lista...'
                        : `${savedImport.importedCount} importados, ${savedImport.skippedCount} omitidos`}
                    </div>
                    {savedImport.sourceType === 'url' && savedImport.sourceValue && (
                      <div className="mt-1 truncate text-xs text-indigo-700" title={savedImport.sourceValue}>
                        URL guardada
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Canales cargados</h2>
                <p className="text-sm text-gray-600">{selectedCount} de {rows.length} seleccionados</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => toggleAll(true)}>Seleccionar todos</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => toggleAll(false)}>Omitir todos</Button>
                <Button type="button" size="sm" onClick={() => setIsImportModalOpen(true)} disabled={!canImport}>
                  <Upload className="mr-2 h-4 w-4" />
                  Importar
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-2 py-2 text-left">
                      <input type="checkbox" checked={selectedCount === rows.length} onChange={(event) => toggleAll(event.target.checked)} className="rounded border-gray-300 text-indigo-600" />
                    </th>
                    <th className="w-40 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Numero</th>
                    <th className="w-52 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Logo</th>
                    <th className="min-w-56 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Nombre</th>
                    <th className="min-w-52 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Pais</th>
                    <th className="min-w-64 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Categorias</th>
                    <th className="w-36 px-2 py-2 text-left text-xs font-medium uppercase text-gray-500">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map((row, index) => (
                    <tr key={row.id} className={!row.selected ? 'bg-gray-50 opacity-70' : undefined}>
                      <td className="px-2 py-2 align-top">
                        <input type="checkbox" checked={row.selected} onChange={(event) => updateRow(row.id, { selected: event.target.checked })} className="rounded border-gray-300 text-indigo-600" />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="space-y-1">
                          <div className="flex">
                            <input
                              type="number"
                              min="0"
                              value={row.channelNumber ?? ''}
                              onChange={(event) => updateRow(row.id, { channelNumber: event.target.value === '' ? null : Number(event.target.value) })}
                              className={`w-20 rounded-l-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 ${numberErrors[row.id] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`}
                            />
                            <Button type="button" size="xs" variant="outline" className="rounded-l-none border-l-0" onClick={() => applySequence(index)}>
                              Secuencia
                            </Button>
                          </div>
                          {validatingNumbers[row.id] && <div className="text-xs text-gray-500">Validando...</div>}
                          {numberErrors[row.id] && <div className="text-xs text-red-600">{numberErrors[row.id]}</div>}
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <ImageUrlUploadField
                          value={row.logoUrl || ''}
                          onChange={(value) => updateRow(row.id, { logoUrl: value })}
                          source={MediaSource.TV_LOGO}
                          placeholder="Logo del canal"
                          showPreview={false}
                          size="sm"
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          value={row.title}
                          onChange={(event) => updateRow(row.id, { title: event.target.value })}
                          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex">
                          <CountryDropdown
                            value={row.countryId || ''}
                            onChange={(value) => updateRow(row.id, { countryId: value })}
                            placeholder="Pais"
                            className="min-w-0 flex-1 [&_button]:rounded-r-none [&_button]:py-1.5 [&_button]:text-sm"
                          />
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            className="h-9 rounded-l-none border-l-0 px-2"
                            onClick={() => applyCountryFromRow(index)}
                            title="Aplicar este pais desde este canal en adelante"
                          >
                            Aplicar
                          </Button>
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <MultiSelect
                          value={row.categories || []}
                          onChange={(value) => updateRow(row.id, { categories: value })}
                          options={categoryOptions}
                          placeholder="Categorias"
                          className="[&>div>div:first-child]:min-h-9 [&>div>div:first-child]:px-2 [&>div>div:first-child]:py-1"
                        />
                      </td>
                      <td className="px-2 py-2 align-top text-sm">
                        {row.error ? <span className="text-red-600">{row.error}</span> : row.imported ? <span className="text-green-700">Importado</span> : <span className="text-gray-500">Pendiente</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} title="Confirmar importacion" size="lg" closeOnBackdrop={!isImporting} closeOnEsc={!isImporting}>
        <div className="space-y-4">
          <p className="text-sm text-gray-700">Se importaran {selectedCount} canales seleccionados.</p>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={saveImport} onChange={(event) => setSaveImport(event.target.checked)} className="mt-1 rounded border-gray-300 text-indigo-600" />
            <span>Guardar esta importacion para reutilizarla despues.</span>
          </label>
          {saveImport && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Nombre</label>
              <input
                type="text"
                value={importName}
                onChange={(event) => setImportName(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setIsImportModalOpen(false)} disabled={isImporting}>Cancelar</Button>
            <Button type="button" onClick={() => void handleImport()} loading={isImporting}>Importar canales</Button>
          </div>
        </div>
      </Modal>
    </TVChannelsLayout>
  );
};

export default TVChannelsImport;
